import React, { useEffect, useRef } from 'react';
import { Button, ButtonGroup } from 'reactstrap';

// Bold / italic / underline are lettered, not icons: the admin ships simple-line-icons, which has no
// icon-bold, icon-italic or icon-underline — those class names rendered three blank buttons.
const COMMANDS = [
  { command: 'bold', label: 'B', style: { fontWeight: 700 }, title: 'Bold' },
  { command: 'italic', label: 'I', style: { fontStyle: 'italic', fontFamily: 'serif' }, title: 'Italic' },
  { command: 'underline', label: 'U', style: { textDecoration: 'underline' }, title: 'Underline' },
  { command: 'insertUnorderedList', icon: 'icon-list', title: 'Bullet list' },
  { command: 'insertOrderedList', icon: 'icon-menu', title: 'Numbered list' },
  { command: 'removeFormat', icon: 'icon-ban', title: 'Clear formatting' }
];

/**
 * The Full Description editor (UN-3248): a contentEditable box with a small toolbar.
 *
 * Deliberately dependency-free. The repo ships no rich-text editor, and the field's contract is one
 * HTML string that the server sanitises on the way in (AppAdvertAuthoringService runs every
 * fullDescription through htmlSanitizer), so whatever markup a paste or execCommand produces cannot
 * persist anything the listing would not accept. document.execCommand is deprecated but is still the
 * only zero-dependency way to format a selection, and every browser this admin supports implements it.
 *
 * The box is uncontrolled on purpose: writing innerHTML on every keystroke would move the caret to
 * the end of the text. It is synced only when the incoming value differs from what is on screen —
 * a load, a reset, or a programmatic change.
 */
const RichText = ({ value, onChange, disabled }) => {
  const box = useRef(null);

  useEffect(() => {
    const html = value || '';
    if (box.current && box.current.innerHTML !== html) {
      box.current.innerHTML = html;
    }
  }, [value]);

  const emit = () => {
    if (box.current) onChange(box.current.innerHTML);
  };

  const exec = command => {
    if (disabled) return;
    // Focus first: execCommand acts on the document selection, and clicking a toolbar button that
    // has taken focus itself would otherwise apply the command to nothing.
    box.current.focus();
    document.execCommand(command, false, null);
    emit();
  };

  return (
    <div>
      <ButtonGroup size="sm" className="mb-1">
        {COMMANDS.map(c => (
          <Button
            key={c.command}
            type="button"
            color="light"
            className="border"
            title={c.title}
            disabled={disabled}
            // onMouseDown, not onClick: the default mousedown action blurs the editor and drops the
            // selection before click fires, which made every button a no-op on a fresh selection.
            onMouseDown={e => {
              e.preventDefault();
              exec(c.command);
            }}
          >
            {c.icon ? <i className={c.icon} /> : <span style={c.style}>{c.label}</span>}
          </Button>
        ))}
      </ButtonGroup>
      <div
        ref={box}
        className="form-control"
        style={{ minHeight: 140, overflowY: 'auto' }}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
      />
      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
        Rich text. Markup is sanitised when saved.
      </div>
    </div>
  );
};

export default RichText;
